import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaInfoCircle, FaBuilding, FaClock, FaTrophy, FaPlay, FaStar, FaCalendarAlt, FaRegCalendarCheck } from "react-icons/fa";
import toast from 'react-hot-toast';

import serverURL from "../../config";
import { useAuth } from '../../context/AuthContext';

const getEventStatusInfo = (clp_status) => {
    switch (clp_status) {
        case 'disqualified':
            return {
                text: 'Disqualified',
                title: 'Test Disqualified (Policy Violation)',
                colorClass: 'text-red-500',
                bgClass: 'bg-red-500',
                buttonBg: 'bg-red-50 text-red-500',
                badgeIcon: <span>!</span>,
                ButtonIcon: FaTrophy
            };
        case 'completed':
            return {
                text: 'Completed',
                title: 'Test Completed Successfully',
                colorClass: 'text-campus-success',
                bgClass: 'bg-campus-success',
                buttonBg: 'bg-gray-200 text-gray-500',
                badgeIcon: <span>✓</span>,
                ButtonIcon: FaTrophy
            };
        default:
            return {
                text: 'Expired',
                title: 'Test Event Expired',
                colorClass: 'text-gray-500',
                bgClass: 'bg-gray-500',
                buttonBg: 'bg-gray-200 text-gray-500',
                badgeIcon: <FaClock className="text-[10px]" />,
                ButtonIcon: FaClock
            };
    }
};

const Events = () => {
    const [events, setEvents] = useState([]);
    const [activeTab, setActiveTab] = useState("suggested");
    const { name: authName } = useAuth();
    const navigate = useNavigate();

    // Handle event click - navigate directly to lesson viewer
    const handleEventClick = (evt) => {
        navigate(`/lessons/${evt.id}`);
    };

    useEffect(() => {
        const handleContextMenu = (event) => {
            event.preventDefault();
        };

        const handleKeyDown = (event) => {
            if (
                (event.ctrlKey || event.metaKey) &&
                (event.key === "c" || event.key === "v")
            ) {
                event.preventDefault();
            }
        };

        document.addEventListener("contextmenu", handleContextMenu);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("contextmenu", handleContextMenu);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            const response = await axios.get(
                `${serverURL}/events/registered`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setEvents(response.data);
        } catch (error) {
            console.error("Failed to fetch events:", error);

            if (error.response?.status === 401 || error.response?.status === 403) {
                return;
            }

            toast.error("Failed to fetch events");
        }
    };

    useEffect(() => {
        fetchEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch events when user returns to this page (e.g., after completing a lesson/test)
    useEffect(() => {
        const handleFocus = () => {
            fetchEvents();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter events based on status
    const getSuggestedEvents = () => {
        return events.filter(evt =>
            evt.retest_status === 'pending' ||
            (evt.status !== 'completed' &&
                (evt.clp_status === 'pending' ||
                 evt.clp_status === 'in_progress' ||
                 !evt.clp_status))
        );
    };

    const getCompletedEvents = () => {
        return events.filter(evt =>
            evt.retest_status !== 'pending' &&
            (evt.status === 'completed' || evt.clp_status === 'completed' || evt.clp_status === 'disqualified')
        );
    };

    return (
        <div className="min-h-screen bg-campus-bg">
            {/* Header Section with Tabs */}
            <header className="bg-gray-50 border-b border-campus-border sticky top-0 z-30">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        {/* Page Title */}
                        <div>
                            <h1 className="text-2xl md:text-3xl font-heading font-bold text-campus-text-primary">
                                Hello, {authName || 'User'}!
                            </h1>
                            <p className="text-sm text-campus-text-body mt-1">
                                Track your test and learning journey
                            </p>
                        </div>

                        {/* Filter Tabs */}
                        <div className="bg-gray-50 border border-campus-border flex gap-1 rounded-pill p-1.5 shadow-card w-full sm:w-auto">
                            <button
                                onClick={() => setActiveTab("suggested")}
                                className={`flex-1 sm:flex-none px-6 py-2.5 rounded-pill font-medium transition-all duration-300 ease-in-out text-sm ${activeTab === "suggested"
                                    ? "bg-campus-primary text-white shadow-glow"
                                    : "bg-transparent text-campus-text-body hover:text-campus-text-primary hover:bg-white"
                                    }`}
                            >
                                Suggested
                            </button>
                            <button
                                onClick={() => setActiveTab("completed")}
                                className={`flex-1 sm:flex-none px-6 py-2.5 rounded-pill font-medium transition-all duration-300 ease-in-out text-sm ${activeTab === "completed"
                                    ? "bg-campus-primary text-white shadow-glow"
                                    : "bg-transparent text-campus-text-body hover:text-campus-text-primary hover:bg-white"
                                    }`}
                            >
                                Completed
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
                {/* Suggested Tab */}
                {activeTab === "suggested" && (
                    <section>
                        {getSuggestedEvents().length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                                {getSuggestedEvents().map((evt, index) => (
                                    <article
                                        key={`suggested-${evt.id}-${index}`}
                                        className="bg-white rounded-card shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden group hover:-translate-y-1 border border-campus-border animate-slide-in flex flex-col"
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        {/* Card Header */}
                                        <div className="relative p-5 md:p-6 bg-gradient-to-br from-red-50 to-white border-b border-campus-border">
                                            <div className="pr-24">
                                                <h3 className="text-lg md:text-xl font-heading font-bold text-campus-text-primary mb-2 line-clamp-2">
                                                    {evt.name}
                                                </h3>
                                                <div className="flex items-center gap-2 text-campus-text-body">
                                                    <FaBuilding className="text-sm flex-shrink-0" />
                                                    <span className="text-sm truncate">{evt.institution}</span>
                                                </div>
                                            </div>

                                            {/* Badge */}
                                            {evt.retest_status === 'pending' ? (
                                                <div className="absolute top-4 right-4 px-3 md:px-4 py-1.5 rounded-pill text-xs font-semibold shadow-sm bg-amber-200 text-black flex items-center gap-1.5">
                                                    <span className="animate-pulse">●</span> Retest Available
                                                </div>
                                            ) : (
                                                <div className="absolute top-4 right-4 px-3 md:px-4 py-1.5 rounded-pill text-xs font-semibold shadow-sm bg-blue-500 text-white">
                                                    Suggested
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Body */}
                                        <div className="px-5 md:px-6 py-4 flex-1 flex flex-col">
                                            {evt.type === "course" ? (
                                                <div className="mb-4">
                                                    <div className="flex items-center justify-between gap-3 mb-2">
                                                        <div className="flex-1 bg-gray-100 rounded-pill h-2.5 overflow-hidden">
                                                            <div
                                                                className="bg-gradient-to-r from-campus-primary to-campus-secondary h-full rounded-pill transition-all duration-500"
                                                                style={{ width: `${evt.completionProgress}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-sm font-semibold text-campus-text-primary whitespace-nowrap">
                                                            {evt.completionProgress}%
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-campus-text-body">Course Progress</p>
                                                </div>
                                            ) : (
                                                <div className="mb-4">
                                                    <h4 className="text-sm font-semibold mb-2 text-campus-text-primary">
                                                        Online Test Event
                                                    </h4>
                                                    <div className="flex items-center gap-2 text-campus-text-body text-sm">
                                                        <FaClock className="flex-shrink-0" />
                                                        <span>Duration: {evt.duration} minutes</span>
                                                    </div>
                                                    {evt.event_date && (
                                                        <div className="flex items-center gap-2 text-campus-text-body text-sm mt-1">
                                                            <FaCalendarAlt className="flex-shrink-0 text-gray-500" />
                                                            <span>End Date: {formatDate(evt.event_date)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Card Footer */}
                                            <div className="flex items-center justify-between pt-4 mt-auto border-t border-campus-border">
                                                <div className="flex items-center gap-1.5 text-yellow-500">
                                                    <span className="text-sm font-semibold text-campus-text-body">
                                                        {evt.rating}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => handleEventClick(evt)}
                                                    className="px-5 md:px-6 py-2.5 rounded-pill transform transition-all duration-300 flex items-center gap-2 font-semibold text-sm bg-campus-primary hover:bg-campus-primary-dark text-white hover:scale-105 hover:shadow-glow"
                                                    aria-label={`Start ${evt.name}`}
                                                >
                                                    <FaPlay className="text-xs" />
                                                    <span>Start</span>
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 md:py-24">
                                <div className="text-center max-w-md">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                        <FaInfoCircle className="text-3xl text-gray-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-campus-text-primary mb-2">
                                        No Suggested Events
                                    </h3>
                                    <p className="text-campus-text-body">
                                        Check back later for new learning opportunities.
                                    </p>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* Completed Tab */}
                {activeTab === "completed" && (
                    <section>
                        {getCompletedEvents().length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                                {getCompletedEvents().map((evt, index) => {
                                    const statusInfo = getEventStatusInfo(evt.clp_status);
                                    const ButtonIcon = statusInfo.ButtonIcon;
                                    
                                    return (
                                    <article
                                        key={`completed-${evt.id}-${index}`}
                                        className="bg-white rounded-card shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden group border border-campus-border animate-slide-in flex flex-col"
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        {/* Card Header */}
                                        <div className="relative p-5 md:p-6 bg-gradient-to-br from-green-50 to-white border-b border-campus-border">
                                            <div className="pr-24">
                                                <h3 className="text-lg md:text-xl font-heading font-bold text-campus-text-primary mb-2 line-clamp-2">
                                                    {evt.name}
                                                </h3>
                                                <div className="flex items-center gap-2 text-campus-text-body">
                                                    <FaBuilding className="text-sm flex-shrink-0" />
                                                    <span className="text-sm truncate">{evt.institution}</span>
                                                </div>
                                            </div>

                                            <div className={`absolute top-4 right-4 px-3 md:px-4 py-1.5 rounded-pill text-xs font-semibold ${statusInfo.bgClass} text-white flex items-center gap-1.5 shadow-sm`}>
                                                {statusInfo.badgeIcon}
                                                <span>{statusInfo.text}</span>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div className="px-5 md:px-6 py-4 flex-1 flex flex-col">
                                            {evt.type === "course" ? (
                                                <div className="mb-4">
                                                    <div className="flex items-center justify-between gap-3 mb-2">
                                                        <div className="flex-1 bg-gray-100 rounded-pill h-2.5 overflow-hidden">
                                                            <div
                                                                className="bg-campus-success h-full rounded-pill"
                                                                style={{ width: '100%' }}
                                                            />
                                                        </div>
                                                        <span className="text-sm font-semibold text-campus-success whitespace-nowrap">
                                                            100%
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-campus-text-body">Course Completed</p>
                                                </div>
                                            ) : (
                                                <div className="mb-4">
                                                    <h4 className={`text-sm font-semibold mb-2 ${statusInfo.colorClass}`}>
                                                        {statusInfo.title}
                                                    </h4>
                                                    <div className="flex items-center gap-2 text-campus-text-body text-sm">
                                                        <FaClock className="flex-shrink-0" />
                                                        <span>Duration: {evt.duration} minutes</span>
                                                    </div>
                                                    {evt.completed_date && (
                                                        <div className="flex items-center gap-2 text-campus-text-body text-sm mt-1">
                                                            <FaRegCalendarCheck className="flex-shrink-0 text-green-500" />
                                                            <span>Completed on: {formatDate(evt.completed_date)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Card Footer */}
                                            <div className="flex items-center justify-between pt-4 mt-auto border-t border-campus-border">
                                                <div className="flex items-center gap-1.5 text-yellow-500">
                                                    <FaStar className="text-sm" />
                                                    <span className="text-sm font-semibold text-campus-text-body">
                                                        {evt.rating}
                                                    </span>
                                                </div>
                                                <button
                                                    disabled
                                                    className={`px-5 md:px-6 py-2.5 rounded-pill flex items-center gap-2 font-semibold text-sm cursor-not-allowed ${statusInfo.buttonBg}`}
                                                    aria-label={`Event ${statusInfo.text.toLowerCase()}`}
                                                >
                                                    <ButtonIcon className="text-xs" />
                                                    <span>{statusInfo.text}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 md:py-24">
                                <div className="text-center max-w-md">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                        <FaTrophy className="text-3xl text-gray-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-campus-text-primary mb-2">
                                        No Completed Events
                                    </h3>
                                    <p className="text-campus-text-body">
                                        Complete your first event to see it here.
                                    </p>
                                </div>
                            </div>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
};

export default Events;
